/**
 * 지표 설명. (3.11)
 *
 * 화면에 숫자만 있으면 몇 달 뒤에 "이격도 96 이 좋은 건가" 를 알 수 없다.
 * 한 줄 힌트는 이미 있었지만, 그 한 줄도 "20일선" 같은 말을 이미 안다고 전제한다.
 *
 * 🔴 전문용어를 풀어 쓴다
 *   이 대시보드를 쓰는 사람은 전업 트레이더가 아니다.
 *   이동평균·볼린저밴드·과매도를 설명 없이 쓰면 숫자를 그냥 믿게 되는데,
 *   그것이 이 프로젝트가 가장 피하려는 상태다.
 *   "왜 이 종목인가" 를 스스로 판단할 수 있어야 한다.
 *
 * 데이터로 분리한 이유
 *   추천 상세와 종목 상세 모달이 같은 목록을 쓴다. 두 곳에 각자 두면
 *   설명이 갈린다 — 한쪽만 고치면 같은 지표를 화면마다 다르게 설명하게 된다.
 *   (IndicatorGrid 를 공통으로 옮긴 것과 같은 이유, CLAUDE.md 5.2절)
 *
 * 계산식과 임계값 근거는 docs/impl/14-technical-indicator.md 와 20-indicator-distribution.md.
 */

export interface IndicatorHelp {
  /** 화면에 뜨는 이름 */
  label: string;
  /** 값 옆의 한 줄. 짧아야 한다 */
  hint: string;
  /** ? 를 눌렀을 때. 문단 단위로 나눠 담는다 */
  detail: string[];
}

/**
 * 지표별 설명.
 *
 * 키는 화면이 쓰는 식별자다. Indicator 타입의 필드명과 맞춰 두면
 * 새 지표를 추가할 때 어느 것이 빠졌는지 바로 보인다.
 */
export const INDICATOR_HELP = {
  atrRatio: {
    label: '변동성(ATR 비율)',
    hint: '하루에 보통 몇 % 움직이는 종목인가',
    detail: [
      '최근 14일 동안 하루에 위아래로 얼마나 움직였는지를 평균 낸 값입니다. 종가 대비 몇 %인지로 바꿔서 보여줍니다.',
      '이 값이 크면 하루 안에 목표가까지 갈 가능성도 크지만, 반대로 손절가까지 떨어질 가능성도 같이 큽니다. 짧게 사고파는 전략에서는 어느 정도 움직여 주는 종목이어야 합니다.',
      '너무 조용한 종목은 목표가에 닿기 전에 15영업일이 지나 버립니다. 그래서 하한을 3%로 두고, 7.6% 근처를 가장 좋은 값으로 봅니다.',
      // 근거는 trading.yml atr-ratio-min 주석 (2026-08-19 저녁 재측정). 전에는 "804종목 중앙값 5.2%" 라고 적혀 있었는데 출처가 없었다 (2026-09-19 I-59)
      '실제로 점수를 받는 종목들(하드 필터를 모두 통과한 2.1년치 569건)을 재보니 중앙값이 4.9%였습니다. 7.6%는 그중 상위 10%에 해당하는 자리입니다.',
    ],
  },

  disparity20: {
    label: '이격도(20일)',
    hint: '최근 20일 평균 가격보다 지금 얼마나 싼가',
    detail: [
      '최근 20일 종가를 평균 낸 값과 지금 가격을 비교한 숫자입니다. 100이면 딱 평균만큼, 96이면 평균보다 4% 싸다는 뜻입니다.',
      '이 전략은 "평소보다 조금 눌린" 자리를 찾습니다. 92~98 구간만 후보로 봅니다.',
      '98을 넘으면 아직 충분히 안 눌린 것으로 보고, 92 아래로 내려가면 잠깐 눌린 것이 아니라 계속 떨어지는 중일 가능성이 큽니다. 싸다고 다 사는 것이 아닙니다.',
      '🔴 실제로 재보니 후보에서 탈락하는 종목의 87%가 이 조건에서 걸립니다. 사실상 이 지표 하나가 후보를 정합니다.',
    ],
  },

  bollingerPb: {
    label: '볼린저 %B',
    hint: '최근 가격 범위에서 지금이 아래쪽인가 위쪽인가',
    detail: [
      '최근 20일 가격이 오르내린 범위를 위아래 선으로 그린 다음, 지금 가격이 그 사이 어디쯤인지를 0~1로 나타낸 값입니다.',
      '0이면 범위의 맨 아래, 1이면 맨 위, 0.5면 한가운데입니다. 1을 넘거나 0 아래로 갈 수도 있습니다 — 평소 범위를 벗어났다는 뜻입니다.',
      '이 전략은 0.30 이하만 봅니다. 최근 흐름에서 아래쪽에 있는 자리입니다.',
      '위의 이격도와 비슷한 것을 다른 방식으로 재는 값이라, 둘이 대체로 같이 움직입니다. 그래서 이격도 조건을 풀어도 후보가 크게 늘지 않았습니다.',
    ],
  },

  rsi14: {
    label: 'RSI(14)',
    hint: '최근 14일 동안 오른 날과 내린 날의 힘 비교',
    detail: [
      '최근 14일 동안 오른 날의 상승폭과 내린 날의 하락폭을 견줘 0~100으로 나타낸 값입니다.',
      '50이면 오르내림이 비슷했다는 뜻이고, 낮을수록 내린 날의 힘이 셌다는 뜻입니다. 흔히 30 아래를 "많이 팔렸다"고 봅니다.',
      '다만 이 값이 낮다는 것이 곧 오를 때가 됐다는 뜻은 아닙니다. 계속 떨어지는 종목은 낮은 값을 오래 유지합니다.',
      '이 전략은 30~48 구간만 후보로 봅니다. 그 밖은 후보에서 빠지고, 구간 안에서는 점수에도 반영됩니다.',
    ],
  },

  rangePosition: {
    label: '레인지 위치',
    hint: '최근 고점·저점 사이에서 지금 위치',
    detail: [
      // 서버 값은 % 다 (IndicatorResponse.rangePosition, 선별 기준 range-position-max: 45). 전에는 "0~1" 로 적혀 있었다 (2026-09-19 I-59)
      '최근 60일의 가장 높았던 가격과 가장 낮았던 가격 사이에서 지금 가격이 어디쯤인지를 0~100(%)으로 나타낸 값입니다.',
      '0이면 그 구간의 바닥, 100이면 꼭대기입니다. 자동 선별은 45 이하(아래쪽 절반 안쪽)만 통과시킵니다.',
      '아래쪽일수록 목표가까지 올라갈 여지가 크다고 보지만, 바닥에 있다는 것 자체가 오를 이유는 아닙니다. 다른 조건과 함께 봅니다.',
    ],
  },

  avgTradeAmount: {
    label: '거래대금(20일 평균)',
    hint: '하루에 보통 얼마어치 거래되는가',
    detail: [
      '최근 20일 동안 하루 평균 얼마어치가 사고팔렸는지입니다.',
      '이 값이 작으면 사려고 할 때 원하는 가격에 파는 사람이 없어 비싸게 사게 되고, 팔 때도 마찬가지입니다. 화면에 보이는 목표 수익률과 실제로 손에 들어오는 금액이 벌어집니다.',
      'KOSPI 안에서도 편차가 큽니다. 804종목의 하루 평균 거래대금 중앙값이 9억원이라, 하루 300억원 미만은 후보에서 제외합니다. 이 조건 하나로 대부분이 걸러집니다.',
    ],
  },
} as const satisfies Record<string, IndicatorHelp>;

export type IndicatorKey = keyof typeof INDICATOR_HELP;

/**
 * 도달 확률 설명.
 *
 * 🔴 지표와 따로 둔다. 이것은 계산된 지표가 아니라 과거 기록이다
 *   같은 목록에 섞으면 "지표 중 하나" 로 읽히고, 그러면 예측처럼 보인다.
 *   화면에서 가장 오해받기 쉬운 숫자다. (CLAUDE.md 1.3절)
 */
export const HIT_PROBABILITY_HELP: IndicatorHelp = {
  label: '과거 도달률',
  hint: '예전에 비슷했던 경우 중 목표가에 닿았던 비율',
  detail: [
    '🔴 이 숫자는 예측이 아닙니다. 앞으로 오를 확률이 아닙니다.',
    '지난 3년 데이터에서 지금과 지표가 비슷했던 시점들을 찾아, 그중 몇 %가 손절가보다 목표가에 먼저 닿았는지를 센 값입니다. 이미 일어난 일을 센 것입니다.',
    '그래서 "70%"는 "이 종목이 70% 확률로 오른다"가 아니라, "예전에 비슷했던 경우 100번 중 70번은 목표가에 먼저 닿았다"는 뜻입니다.',
    '🔴 표본 수를 반드시 함께 보세요. 8건 중 5건(62%)과 500건 중 310건(62%)은 전혀 다른 이야기입니다. 비슷했던 경우가 20건보다 적으면 숫자를 내지 않고 "과거 표본 없음"이라고 적고, 30건보다 적으면 숫자를 흐리게 보여줍니다.',
    '이 값이 실제와 맞는지는 지난 추천 화면의 "예상 적중도"에서 계속 확인하고 있습니다.',
  ],
};

/**
 * 점수 설명.
 *
 * 화면에 가장 크게 뜨는 숫자인데 그 자리에는 설명이 없었다.
 */
export const SCORE_HELP: IndicatorHelp = {
  label: '점수',
  hint: '조건을 얼마나 잘 만족하는지 0~100으로 환산한 값',
  detail: [
    '위 지표들을 각각 점수로 바꾼 뒤 가중치를 곱해 더한 값입니다. 55점 이상만 추천으로 내보냅니다.',
    '🔴 그날 종목들 사이의 순위가 아니라 절대 기준입니다. 같은 55점은 어느 날에 나와도 같은 뜻입니다.',
    '순위로 매기면 조건이 나쁜 날에도 1등은 나오게 되어, 살 만한 종목이 없는 날에도 무언가를 추천하게 됩니다.',
    '지난 3년 중 가장 높았던 점수가 77.1점입니다. 90점대는 나오지 않습니다.',
    '점수가 높다고 반드시 오르는 것은 아닙니다. 조건에 잘 맞는다는 뜻일 뿐이고, 실제로 맞았는지는 지난 추천 화면에서 확인합니다.',
  ],
};
